// ============================================================
// FONADHOO CRM v3.0 - ADVANCED SEARCH COMPONENT
// ============================================================
// Search-first voter discovery: name/ID/phone search box at the top,
// five collapsible filter groups (Location, Demographics, Political,
// Outreach Status, Family), active filter pills, and a results list
// that only renders once at least one criterion is set.
//
// Architecture:
//   - All filtering happens client-side over `allVoters`.
//   - Outreach data and family data are fetched once on mount and
//     joined to voters by id when their respective filters are active.
//   - Mobile vs desktop is purely a CSS / collapse-default difference;
//     the same component runs on both.
// ============================================================

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  hasEffectivePermission,
  SUPPORT_LEVELS,
  GENDER_OPTIONS,
  OCCUPATION_CATEGORIES,
  ISSUE_OPTIONS,
  OUTREACH_TYPES,
} from './shared/constants';
import SearchableSelect from './shared/SearchableSelect';
import { Icon } from './shared/Icon';
import { deriveFamilyName, calculateAge } from './shared/utils';

// Party Meta map — same source of truth used by VoterProfile. Values are
// the short codes actually stored in `voters.party_status` (MDP, PNC, etc.).
// PARTY_OPTIONS in constants.js is OUT OF DATE — it lists labels like
// "MDP Member" / "PPM Member" that don't match any value in the database.
// Filtering against those produces zero matches; we use PM instead.
const PARTY_META = {
  MDP:       'MDP',
  PNC:       'PNC',
  AP:        'AP',
  JP:        'JP',
  MDA:       'MDA',
  MLCD:      'MLCD',
  MNP:       'MNP',
  Democrats: 'Democrats',
  PNF:       'PNF',
  none:      'No Party',
};

// Same support-level colour palette used elsewhere in the app.
const SC = {
  'Strong Support':  { bg:'var(--sup-strong-bg)',       text:'var(--sup-strong-text)',       dot:'var(--sup-strong-dot)' },
  'Leaning Support': { bg:'var(--sup-lean-bg)',         text:'var(--sup-lean-text)',         dot:'var(--sup-lean-dot)' },
  'Undecided':       { bg:'var(--sup-undecided-bg)',    text:'var(--sup-undecided-text)',    dot:'var(--sup-undecided-dot)' },
  'Leaning Against': { bg:'var(--sup-against-lean-bg)', text:'var(--sup-against-lean-text)', dot:'var(--sup-against-lean-dot)' },
  'Strong Against':  { bg:'var(--sup-against-bg)',      text:'var(--sup-against-text)',      dot:'var(--sup-against-dot)' },
};

// Default empty criteria — used both as initial state and to reset.
const EMPTY_CRITERIA = {
  searchText: '',
  // Location (cascading)
  atoll: 'All',
  island: 'All',
  ward: 'All',
  house: 'All',
  ballotR1: 'All',
  ballotR2: 'All',
  // Demographics
  ageMin: '',
  ageMax: '',
  gender: 'All',
  occupation: 'All',
  // Political
  support: 'All',
  party: 'All',
  issues: [], // multi-select
  influencerOnly: false,
  // Outreach
  contacted: 'Any',          // 'Any' | 'Yes' | 'No'
  lastContactWithin: 'Any',  // 'Any' | '7' | '30' | '90' | 'Never'
  outreachType: 'All',
  // Family
  hasFamily: 'Any',          // 'Any' | 'Yes' | 'No'
  familyId: 'All',
  familyRole: 'All',
};

// Shared input/select style — keeps every form control visually consistent
// without repeating the dozen properties on each render.
const inputStyle = {
  width: '100%',
  padding: '9px 11px',
  borderRadius: 7,
  border: '1px solid var(--border)',
  background: 'var(--surface-2)',
  color: 'var(--text-strong)',
  fontSize: 12,
  fontFamily: "'DM Mono', monospace",
  boxSizing: 'border-box',
  outline: 'none',
};

const labelStyle = {
  display: 'block',
  fontSize: 10,
  marginBottom: 5,
  color: 'var(--text-2)',
  fontFamily: "'DM Mono', monospace",
};

// ────────────────────────────────────────────────────────────────
// FilterGroup — a collapsible card for one set of related filters.
// Header (always visible) shows icon + title + a small "(N active)"
// badge when at least one filter inside is non-default.
// ────────────────────────────────────────────────────────────────
function FilterGroup({ icon, title, activeCount, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{
      background: 'var(--surface)',
      border: '1px solid ' + (activeCount > 0 ? '#3b82f6' : 'var(--border)'),
      borderRadius: 8,
      marginBottom: 8,
      // overflow: 'visible' — IMPORTANT. Lets the SearchableSelect's
      // absolutely-positioned dropdown popup escape this card. With
      // 'hidden' (the original CSS for clean rounded corners), the
      // popup got clipped and only one option was visible.
      overflow: 'visible',
      transition: 'border-color 0.15s',
      // Note: previously we had `position: relative` and `zIndex: 5`
      // here when the group was open. That created a stacking context
      // that boxed in the child popup, causing it to overlap nearby
      // groups oddly when multiple groups were open at once. Removing
      // these lets the popup's own zIndex:50 work normally relative to
      // the page.
    }}>
      <button
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 10,
          padding: '12px 14px',
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          textAlign: 'left',
          color: 'inherit',
        }}
      >
        <span style={{
          fontFamily: "'Cormorant Garamond', serif",
          fontSize: 14,
          color: 'var(--link)',
          fontWeight: 600,
          display: 'flex',
          alignItems: 'center',
          gap: 8,
        }}>
          <Icon name={icon} size={15} color="var(--link)" />
          <span>{title}</span>
          {activeCount > 0 && (
            <span style={{
              fontSize: 9,
              padding: '2px 7px',
              borderRadius: 10,
              background: 'var(--border)',
              color: 'var(--link)',
              border: '1px solid #3b82f6',
              fontFamily: "'DM Mono', monospace",
              fontWeight: 500,
            }}>
              {activeCount} active
            </span>
          )}
        </span>
        <span style={{
          color: 'var(--text-3)',
          fontSize: 14,
          lineHeight: 1,
          transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
          transition: 'transform 0.15s',
        }}>▾</span>
      </button>
      {open && (
        <div style={{ padding: '0 14px 14px 14px' }}>
          {children}
        </div>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ────────────────────────────────────────────────────────────────
export default function AdvancedSearch({ supabase, user, allVoters = [], allLocations = [] }) {
  const profile = user ?? null;
  const canSeeSupport = hasEffectivePermission(profile, 'canViewVotes');
  const isSimpleMode = !hasEffectivePermission(profile, 'canViewAll');
  const navigate = useNavigate();
  const location = useLocation();

  // Pre-populate criteria from navigation state so that clicking a support
  // level row or metric card on the home page lands with the filter already set.
  // showAll: true bypasses the "at least one criterion" gate and shows every voter.
  const [showAll, setShowAll] = useState(() => !!location.state?.showAll);
  const [criteria, setCriteria] = useState(() => {
    const s = location.state;
    return s ? { ...EMPTY_CRITERIA, ...s } : EMPTY_CRITERIA;
  });

  // Outreach + family data fetched once on mount. We keep them as plain
  // arrays/maps and join client-side when their filters are active.
  const [outreachByVoter, setOutreachByVoter] = useState({});
  const [families, setFamilies] = useState([]);
  const [familyMembers, setFamilyMembers] = useState([]);

  // Saved searches, result sort, and bulk selection.
  const [savedSegments, setSavedSegments] = useState([]);
  const [sortBy, setSortBy] = useState('name'); // name | island | age | support

  const loadSegments = async () => {
    const { data } = await supabase.from('saved_segments').select('*').order('created_at', { ascending: false });
    setSavedSegments(data || []);
  };
  useEffect(() => { loadSegments(); /* eslint-disable-next-line */ }, [supabase]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Outreach: pull all rows, group by voter_id, pre-compute most recent
      // date for "last contact within X" filter.
      // (Outreach TYPES list is canonical — pulled from constants — not
      // derived from existing outreach rows. That way every filterable
      // type is offered even if no voter has been contacted that way yet.)
      const { data: out } = await supabase
        .from('outreach')
        .select('voter_id, type, date');
      if (!cancelled && out) {
        const map = {};
        for (const row of out) {
          if (!map[row.voter_id]) map[row.voter_id] = { count: 0, latest: null, types: new Set() };
          map[row.voter_id].count += 1;
          if (!map[row.voter_id].latest || row.date > map[row.voter_id].latest) {
            map[row.voter_id].latest = row.date;
          }
          if (row.type) map[row.voter_id].types.add(row.type);
        }
        setOutreachByVoter(map);
      }

      // Families + their members for the Family filter group. We pull
      // parent_ids and spouse_id too so deriveFamilyName can identify
      // the founding couple/root for the auto-generated name.
      // We use select('*') because family naming column varies across
      // installs — schema says `name` but some legacy code references
      // `family_name`. We don't actually use either field (names are
      // derived from members), but a wrong column name in the select
      // would make Supabase silently return null and the filter dropdown
      // would appear permanently disabled.
      const { data: fams } = await supabase.from('families').select('*');
      if (!cancelled && fams) setFamilies(fams);
      const { data: mems } = await supabase.from('family_members').select('id, voter_id, family_id, role, parent_ids, spouse_id');
      if (!cancelled && mems) setFamilyMembers(mems);
    })();
    return () => { cancelled = true; };
  }, [supabase]);

  // ────────────────────────────────────────────────────────────
  // Cascading Location options.
  // The dropdown for Ward only shows wards whose voters live on the
  // selected island. Same for House (filtered by island+ward). And
  // ballot boxes follow the full chain.
  // ────────────────────────────────────────────────────────────
  const atolls = useMemo(() => {
    const s = new Set();
    for (const l of allLocations) s.add(l.atoll);
    for (const v of allVoters) if (v.atoll) s.add(v.atoll);
    return Array.from(s).sort();
  }, [allLocations, allVoters]);

  const islands = useMemo(() => {
    const s = new Set();
    for (const l of allLocations) {
      if (criteria.atoll !== 'All' && l.atoll !== criteria.atoll) continue;
      s.add(l.island);
    }
    for (const v of allVoters) {
      if (criteria.atoll !== 'All' && v.atoll !== criteria.atoll) continue;
      if (v.island) s.add(v.island);
    }
    return Array.from(s).sort();
  }, [allLocations, allVoters, criteria.atoll]);

  const wardsForIsland = useMemo(() => {
    const s = new Set();
    for (const v of allVoters) {
      if (criteria.atoll !== 'All' && v.atoll !== criteria.atoll) continue;
      if (criteria.island !== 'All' && v.island !== criteria.island) continue;
      if (v.ward) s.add(v.ward);
    }
    return Array.from(s).sort((a, b) => String(a).localeCompare(String(b), undefined, { numeric: true }));
  }, [allVoters, criteria.atoll, criteria.island]);

  const housesForWard = useMemo(() => {
    const s = new Set();
    for (const v of allVoters) {
      if (criteria.island !== 'All' && v.island !== criteria.island) continue;
      if (criteria.ward !== 'All' && v.ward !== criteria.ward) continue;
      if (v.house) s.add(v.house);
    }
    return Array.from(s).sort();
  }, [allVoters, criteria.island, criteria.ward]);

  const ballotR1Options = useMemo(() => {
    const s = new Set();
    for (const v of allVoters) {
      if (criteria.island !== 'All' && v.island !== criteria.island) continue;
      if (criteria.ward !== 'All' && v.ward !== criteria.ward) continue;
      if (v.ballot_box_r1) s.add(v.ballot_box_r1);
    }
    return Array.from(s).sort();
  }, [allVoters, criteria.island, criteria.ward]);

  const ballotR2Options = useMemo(() => {
    const s = new Set();
    for (const v of allVoters) {
      if (criteria.island !== 'All' && v.island !== criteria.island) continue;
      if (criteria.ward !== 'All' && v.ward !== criteria.ward) continue;
      if (v.ballot_box_r2) s.add(v.ballot_box_r2);
    }
    return Array.from(s).sort();
  }, [allVoters, criteria.island, criteria.ward]);

  // Helpers to mutate criteria. setField is the standard one. setIsland
  // and setWard ALSO reset their dependent fields (ward, house, ballot
  // boxes, AND specific family — all cascading from location), so a
  // stale selection from a different island can't silently filter
  // results.
  const setField = (key, value) => setCriteria(c => ({ ...c, [key]: value }));
  const setAtoll = (value) =>
    setCriteria(c => ({ ...c, atoll: value, island: 'All', ward: 'All', house: 'All', ballotR1: 'All', ballotR2: 'All', familyId: 'All' }));
  const setIsland = (value) =>
    setCriteria(c => ({ ...c, island: value, ward: 'All', house: 'All', ballotR1: 'All', ballotR2: 'All', familyId: 'All' }));
  const setWard = (value) =>
    setCriteria(c => ({ ...c, ward: value, house: 'All', ballotR1: 'All', ballotR2: 'All', familyId: 'All' }));
  const toggleIssue = (issue) =>
    setCriteria(c => ({
      ...c,
      issues: c.issues.includes(issue)
        ? c.issues.filter(i => i !== issue)
        : [...c.issues, issue],
    }));

  // Map voter_id → family info for the Family filter group.
  // We coerce ids to strings before comparing because family_members.voter_id
  // and family_members.family_id can come back as either strings or numbers
  // depending on the column type in Supabase (FamilyTree.jsx has the same
  // defensive coercion at line 842). Strict === would miss matches that
  // are conceptually equal.
  const voterFamilyMap = useMemo(() => {
    const map = {};
    for (const m of familyMembers) {
      const vid = String(m.voter_id);
      if (!map[vid]) map[vid] = [];
      map[vid].push({ family_id: String(m.family_id), role: m.role });
    }
    return map;
  }, [familyMembers]);

  const familyRoleOptions = useMemo(() => {
    const s = new Set();
    for (const m of familyMembers) if (m.role) s.add(m.role);
    return Array.from(s).sort();
  }, [familyMembers]);

  // Lookup table voterId -> voter for fast name resolution. Used by the
  // family auto-name logic and by the active-pill display.
  // Keyed by String(id) so a string voter_id from family_members still
  // matches the numeric voter.id.
  const votersById = useMemo(() => {
    const map = {};
    for (const v of allVoters) map[String(v.id)] = v;
    return map;
  }, [allVoters]);

  // Pre-compute every family's display name once. Family names are
  // derived from members (oldest couple at the top of the tree)
  // rather than the manual `family_name` column, which is unreliable.
  const familyNamesById = useMemo(() => {
    // Group members by family_id once, then derive each family's name.
    const byFamily = {};
    for (const m of familyMembers) {
      if (!byFamily[m.family_id]) byFamily[m.family_id] = [];
      byFamily[m.family_id].push(m);
    }
    const out = {};
    for (const f of families) {
      out[f.id] = deriveFamilyName(f, byFamily[f.id] || [], votersById);
    }
    return out;
  }, [families, familyMembers, votersById]);

  // Cascading family list — narrowed by the selected Island and (if set)
  // Ward. Families table has an `island` column; no `ward` column, so
  // ward filtering goes through members → voters: a family is on Ward X
  // if any of its members is a voter living on Ward X.
  const familiesForLocation = useMemo(() => {
    if (criteria.island === 'All') return [];
    // Filter by family.island.
    let pool = families.filter(f =>
      // Some legacy rows might have null/empty island; keep them out.
      f.island && f.island === criteria.island
    );
    if (criteria.ward !== 'All') {
      // Build a set of family_ids that have at least one voter on the
      // selected ward. Single pass through familyMembers.
      const onWard = new Set();
      for (const m of familyMembers) {
        const v = votersById[String(m.voter_id)];
        if (v && v.ward === criteria.ward && v.island === criteria.island) {
          onWard.add(String(m.family_id));
        }
      }
      pool = pool.filter(f => onWard.has(String(f.id)));
    }
    return pool;
  }, [families, familyMembers, votersById, criteria.island, criteria.ward]);

  // ────────────────────────────────────────────────────────────
  // Detect which filters are non-default. Used for: (a) the active
  // count badges on each group header, (b) the active-pills row
  // above the results, and (c) the "do we have any criteria yet?"
  // gate that decides whether to render the results list at all.
  // ────────────────────────────────────────────────────────────
  const activePills = useMemo(() => {
    const pills = [];
    if (criteria.searchText.trim()) {
      pills.push({ key: 'searchText', label: `Search: "${criteria.searchText.trim()}"`, group: 'search' });
    }
    if (criteria.atoll !== 'All')    pills.push({ key: 'atoll', label: `Atoll: ${criteria.atoll}`, group: 'location' });
    if (criteria.island !== 'All')   pills.push({ key: 'island', label: `Island: ${criteria.island}`, group: 'location' });
    if (criteria.ward !== 'All')     pills.push({ key: 'ward', label: `Ward: ${criteria.ward}`, group: 'location' });
    if (criteria.house !== 'All')    pills.push({ key: 'house', label: `House: ${criteria.house}`, group: 'location' });
    if (criteria.ballotR1 !== 'All') pills.push({ key: 'ballotR1', label: `Ballot R1: ${criteria.ballotR1}`, group: 'location' });
    if (criteria.ballotR2 !== 'All') pills.push({ key: 'ballotR2', label: `Ballot R2: ${criteria.ballotR2}`, group: 'location' });

    if (criteria.ageMin)             pills.push({ key: 'ageMin', label: `Age ≥ ${criteria.ageMin}`, group: 'demo' });
    if (criteria.ageMax)             pills.push({ key: 'ageMax', label: `Age ≤ ${criteria.ageMax}`, group: 'demo' });
    if (criteria.gender !== 'All')   pills.push({ key: 'gender', label: `Gender: ${criteria.gender}`, group: 'demo' });

    if (criteria.support !== 'All')  pills.push({ key: 'support', label: `Support: ${criteria.support}`, group: 'political' });
    if (criteria.party !== 'All')    pills.push({ key: 'party', label: `Party: ${PARTY_META[criteria.party] || criteria.party}`, group: 'political' });
    if (criteria.occupation !== 'All') pills.push({ key: 'occupation', label: `Job: ${criteria.occupation}`, group: 'political' });
    for (const i of criteria.issues) pills.push({ key: `issue:${i}`, label: `Issue: ${i}`, group: 'political' });
    if (criteria.influencerOnly) pills.push({ key: 'influencerOnly', label: 'Key Influencers', group: 'political' });

    if (criteria.contacted !== 'Any')         pills.push({ key: 'contacted', label: `Contacted: ${criteria.contacted}`, group: 'outreach' });
    if (criteria.lastContactWithin !== 'Any') pills.push({ key: 'lastContactWithin', label: `Last contact: ${criteria.lastContactWithin === 'Never' ? 'Never' : 'within ' + criteria.lastContactWithin + ' days'}`, group: 'outreach' });
    if (criteria.outreachType !== 'All')      pills.push({ key: 'outreachType', label: `Outreach: ${criteria.outreachType}`, group: 'outreach' });

    if (criteria.hasFamily !== 'Any')   pills.push({ key: 'hasFamily', label: `Has family: ${criteria.hasFamily}`, group: 'family' });
    if (criteria.familyId !== 'All') {
      const name = familyNamesById[criteria.familyId] || familyNamesById[String(criteria.familyId)] || criteria.familyId;
      pills.push({ key: 'familyId', label: `Family: ${name}`, group: 'family' });
    }
    if (criteria.familyRole !== 'All')  pills.push({ key: 'familyRole', label: `Role: ${criteria.familyRole}`, group: 'family' });

    return pills;
  }, [criteria, familyNamesById]);

  const groupActiveCount = (group) => activePills.filter(p => p.group === group).length;
  const hasAnyCriteria = activePills.length > 0 || showAll;

  // ────────────────────────────────────────────────────────────
  // Filter the voters. Each block is a one-line guard that returns
  // false to exclude the voter; if all guards pass, voter is kept.
  // ────────────────────────────────────────────────────────────
  const filteredVoters = useMemo(() => {
    if (!hasAnyCriteria && !isSimpleMode) return [];

    const search = criteria.searchText.trim().toLowerCase();
    const now = Date.now();
    const dayMs = 86400000;

    return allVoters.filter(voter => {
      // Smart search: name + ID + phone
      if (search) {
        const haystack = [
          voter.name, voter.id_number,
          voter.phone, voter.phone2, voter.phone3, voter.whatsapp
        ].filter(Boolean).join(' ').toLowerCase();
        if (!haystack.includes(search)) return false;
      }

      // Location
      if (criteria.atoll !== 'All' && voter.atoll !== criteria.atoll) return false;
      if (criteria.island !== 'All' && voter.island !== criteria.island) return false;
      if (criteria.ward !== 'All' && voter.ward !== criteria.ward) return false;
      if (criteria.house !== 'All' && voter.house !== criteria.house) return false;
      if (criteria.ballotR1 !== 'All' && voter.ballot_box_r1 !== criteria.ballotR1) return false;
      if (criteria.ballotR2 !== 'All' && voter.ballot_box_r2 !== criteria.ballotR2) return false;

      // Demographics — fall back to age computed from dob when voter.age is
      // empty (most rows store dob but not age).
      const effAge = voter.age != null ? voter.age : calculateAge(voter.dob);
      if (criteria.ageMin && (effAge == null || effAge < parseInt(criteria.ageMin))) return false;
      if (criteria.ageMax && (effAge == null || effAge > parseInt(criteria.ageMax))) return false;
      if (criteria.gender !== 'All' && voter.gender !== criteria.gender) return false;
      if (criteria.occupation !== 'All' && voter.occupation_category !== criteria.occupation) return false;

      // Political
      if (criteria.support === 'Supporters') {
        if (voter.support !== 'Strong Support' && voter.support !== 'Leaning Support') return false;
      } else if (criteria.support === 'Against') {
        if (voter.support !== 'Strong Against' && voter.support !== 'Leaning Against') return false;
      } else if (criteria.support !== 'All' && voter.support !== criteria.support) return false;
      if (criteria.party !== 'All' && voter.party_status !== criteria.party) return false;
      if (criteria.issues.length > 0) {
        // Voter must have at least ONE of the selected issues (OR semantics).
        const vIssues = voter.issues || [];
        const matchesAny = criteria.issues.some(i => vIssues.includes(i));
        if (!matchesAny) return false;
      }
      if (criteria.influencerOnly && !voter.influencer) return false;

      // Outreach
      const ov = outreachByVoter[voter.id];
      if (criteria.contacted === 'Yes' && (!ov || ov.count === 0)) return false;
      if (criteria.contacted === 'No' && ov && ov.count > 0) return false;
      if (criteria.lastContactWithin !== 'Any') {
        if (criteria.lastContactWithin === 'Never') {
          if (ov && ov.count > 0) return false;
        } else {
          if (!ov || !ov.latest) return false;
          const daysAgo = (now - new Date(ov.latest).getTime()) / dayMs;
          if (daysAgo > parseInt(criteria.lastContactWithin)) return false;
        }
      }
      if (criteria.outreachType !== 'All') {
        if (!ov || !ov.types.has(criteria.outreachType)) return false;
      }

      // Family
      const fams = voterFamilyMap[String(voter.id)] || [];
      if (criteria.hasFamily === 'Yes' && fams.length === 0) return false;
      if (criteria.hasFamily === 'No' && fams.length > 0) return false;
      if (criteria.familyId !== 'All' && !fams.some(f => f.family_id === String(criteria.familyId))) return false;
      if (criteria.familyRole !== 'All' && !fams.some(f => f.role === criteria.familyRole)) return false;

      return true;
    });
  }, [allVoters, criteria, hasAnyCriteria, outreachByVoter, voterFamilyMap]);

  // ────────────────────────────────────────────────────────────
  // Pill removal. Maps each pill.key back to the criteria field
  // (or array element) that should be reset.
  // ────────────────────────────────────────────────────────────
  const removePill = (pill) => {
    if (pill.key === 'searchText')         return setField('searchText', '');
    if (pill.key === 'atoll')             return setAtoll('All');
    if (pill.key === 'island')             return setIsland('All');
    if (pill.key === 'ward')               return setWard('All');
    if (pill.key === 'house')              return setField('house', 'All');
    if (pill.key === 'ballotR1')           return setField('ballotR1', 'All');
    if (pill.key === 'ballotR2')           return setField('ballotR2', 'All');
    if (pill.key === 'ageMin')             return setField('ageMin', '');
    if (pill.key === 'ageMax')             return setField('ageMax', '');
    if (pill.key === 'gender')             return setField('gender', 'All');
    if (pill.key === 'occupation')         return setField('occupation', 'All');
    if (pill.key === 'support')            return setField('support', 'All');
    if (pill.key === 'party')              return setField('party', 'All');
    if (pill.key.startsWith('issue:'))     return toggleIssue(pill.key.slice(6));
    if (pill.key === 'contacted')          return setField('contacted', 'Any');
    if (pill.key === 'lastContactWithin') return setField('lastContactWithin', 'Any');
    if (pill.key === 'outreachType')       return setField('outreachType', 'All');
    if (pill.key === 'hasFamily')          return setField('hasFamily', 'Any');
    if (pill.key === 'familyId')           return setField('familyId', 'All');
    if (pill.key === 'familyRole')         return setField('familyRole', 'All');
  };

  // Support breakdown of the current result set.
  const resultSummary = useMemo(() => {
    let sup = 0, agn = 0, und = 0;
    for (const v of filteredVoters) {
      if (v.support === 'Strong Support' || v.support === 'Leaning Support') sup++;
      else if (v.support === 'Strong Against' || v.support === 'Leaning Against') agn++;
      else und++;
    }
    return { sup, agn, und };
  }, [filteredVoters]);

  // Sorted copy for display.
  const sortedVoters = useMemo(() => {
    const arr = [...filteredVoters];
    const supRank = { 'Strong Support': 0, 'Leaning Support': 1, 'Undecided': 2, 'Leaning Against': 3, 'Strong Against': 4 };
    const ageOf = (v) => (v.age != null ? v.age : (calculateAge(v.dob) ?? -1));
    const byName = (a, b) => String(a.name || '').localeCompare(String(b.name || ''));
    arr.sort((a, b) => {
      if (sortBy === 'island') return String(a.island || '').localeCompare(String(b.island || '')) || byName(a, b);
      if (sortBy === 'age') return ageOf(b) - ageOf(a);
      if (sortBy === 'support') return ((supRank[a.support] ?? 2) - (supRank[b.support] ?? 2)) || byName(a, b);
      return byName(a, b);
    });
    return arr;
  }, [filteredVoters, sortBy]);

  // ── Saved searches (saved_segments table) ──
  const saveSegment = async () => {
    const name = window.prompt('Name this search');
    if (!name || !name.trim()) return;
    const { error } = await supabase.from('saved_segments').insert({
      name: name.trim(), filters: criteria, voter_count: filteredVoters.length, created_by: profile?.id,
    });
    if (error) { alert('Could not save search: ' + error.message); return; }
    loadSegments();
  };
  const applySegment = (seg) => {
    setCriteria({ ...EMPTY_CRITERIA, ...(seg.filters || {}) });
    setShowAll(false);
  };
  const deleteSegment = async (seg) => {
    if (!window.confirm(`Delete saved search "${seg.name}"?`)) return;
    const { error } = await supabase.from('saved_segments').delete().eq('id', seg.id);
    if (error) { alert('Could not delete: ' + error.message); return; }
    loadSegments();
  };

  // ── CSV export ──
  const exportCsv = (list) => {
    const esc = (s) => `"${String(s == null ? '' : s).replace(/"/g, '""')}"`;
    const rows = [['Name', 'ID Number', 'Island', 'Ward', 'House', 'Phone', 'Gender', 'Age', 'Support', 'Party']];
    for (const v of list) {
      rows.push([v.name, v.id_number, v.island, v.ward, v.house, v.phone, v.gender, (v.age ?? calculateAge(v.dob) ?? ''), canSeeSupport ? v.support : '', v.party_status].map(esc));
    }
    const blob = new Blob([rows.map(r => r.join(',')).join('\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `voters-${new Date().toISOString().split('T')[0]}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  // ────────────────────────────────────────────────────────────
  // RENDER
  // ────────────────────────────────────────────────────────────
  return (
    <div>

      {/* ───── Back button (shown when navigated from another section) ───── */}
      {location.state && (
        <button
          onClick={() => navigate(-1)}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            marginBottom: 10, padding: '7px 14px',
            borderRadius: 7, border: '1px solid var(--border)',
            background: 'var(--surface)', color: 'var(--link)',
            fontSize: 13, cursor: 'pointer', fontFamily: "'DM Mono', monospace",
          }}
          onMouseEnter={e => e.currentTarget.style.borderColor = '#3b82f6'}
          onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border)'}
        >
          <Icon name="arrow" size={14} style={{ transform: 'rotate(180deg)' }} /> Back
        </button>
      )}

      {/* ───── Header + Smart Search Box (boxed header — consistent with the other tabs) ───── */}
      <div style={{
        background: 'var(--surface)',
        border: '1px solid var(--border)',
        boxShadow: 'var(--shadow-sm)',
        borderRadius: 14,
        padding: 20,
        marginBottom: 16,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 11, marginBottom: 16 }}>
          <span style={{ width: 38, height: 38, borderRadius: 11, display: 'grid', placeItems: 'center', background: '#3b82f61f', color: '#3b82f6', flexShrink: 0 }}>
            <Icon name="search" size={20} />
          </span>
          <div>
            <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 10.5, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--text-3)', marginBottom: 2 }}>Find</div>
            <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '26px', fontWeight: 600, margin: 0, color: 'var(--text-strong)', letterSpacing: '-0.3px', lineHeight: 1 }}>{isSimpleMode ? 'Search' : 'Advanced Search'}</h2>
          </div>
        </div>
        <input
          type="text"
          value={criteria.searchText}
          onChange={(e) => setField('searchText', e.target.value)}
          placeholder="Search by name, ID number, or phone…"
          style={{ ...inputStyle, fontSize: 13, padding: '11px 13px' }}
        />
      </div>

      {/* ───── Saved searches ───── */}
      {!isSimpleMode && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
          <button onClick={saveSegment} disabled={!hasAnyCriteria} title={hasAnyCriteria ? 'Save the current filters as a named search' : 'Set a filter first'}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 13px', borderRadius: 9, border: '1px solid var(--border)', background: 'transparent', color: hasAnyCriteria ? 'var(--text-2)' : 'var(--text-4)', fontSize: 12, fontWeight: 600, cursor: hasAnyCriteria ? 'pointer' : 'not-allowed', fontFamily: "'DM Mono', monospace" }}>
            <Icon name="flag" size={13} /> Save this search
          </button>
          {savedSegments.map(seg => (
            <span key={seg.id} style={{ display: 'inline-flex', alignItems: 'center', borderRadius: 14, border: '1px solid var(--border)', background: 'var(--surface-2)', overflow: 'hidden' }}>
              <button onClick={() => applySegment(seg)} title="Apply this saved search"
                style={{ padding: '5px 4px 5px 11px', border: 'none', background: 'transparent', color: 'var(--link)', fontSize: 11, fontFamily: "'DM Mono', monospace", cursor: 'pointer' }}>
                {seg.name}{seg.voter_count != null ? ` (${seg.voter_count})` : ''}
              </button>
              <button onClick={() => deleteSegment(seg)} title="Delete saved search"
                style={{ padding: '5px 9px', border: 'none', background: 'transparent', color: 'var(--text-4)', fontSize: 13, cursor: 'pointer', lineHeight: 1 }}>×</button>
            </span>
          ))}
        </div>
      )}

      {/* ───── Filter Groups (advanced mode only) ───── */}
      {!isSimpleMode && <>
      <FilterGroup icon="mapPin" title="Location" activeCount={groupActiveCount('location')}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 10 }}>
          <div>
            <label style={labelStyle}>Atoll / City</label>
            <SearchableSelect
              value={criteria.atoll === 'All' ? '' : criteria.atoll}
              options={atolls}
              onChange={v => setAtoll(v || 'All')}
              placeholder="All Atolls"
            />
          </div>
          <div>
            <label style={labelStyle}>Island {criteria.atoll !== 'All' && <span style={{ color: 'var(--text-4)' }}>(in {criteria.atoll})</span>}</label>
            <SearchableSelect
              value={criteria.island === 'All' ? '' : criteria.island}
              options={islands}
              onChange={v => setIsland(v || 'All')}
              placeholder="All Islands"
            />
          </div>
          <div>
            <label style={labelStyle}>
              Ward {criteria.island === 'All' && <span style={{ color: 'var(--text-4)' }}>(pick island first)</span>}
            </label>
            <SearchableSelect
              value={criteria.ward === 'All' ? '' : criteria.ward}
              options={wardsForIsland}
              onChange={v => setWard(v || 'All')}
              placeholder="All Wards"
              disabled={criteria.island === 'All'}
            />
          </div>
          <div>
            <label style={labelStyle}>House</label>
            <SearchableSelect
              value={criteria.house === 'All' ? '' : criteria.house}
              options={housesForWard}
              onChange={v => setField('house', v || 'All')}
              placeholder="All Houses"
              disabled={housesForWard.length === 0}
            />
          </div>
          <div>
            <label style={labelStyle}>Ballot Box (Round 1)</label>
            <SearchableSelect
              value={criteria.ballotR1 === 'All' ? '' : criteria.ballotR1}
              options={ballotR1Options}
              onChange={v => setField('ballotR1', v || 'All')}
              placeholder="All Boxes"
              disabled={ballotR1Options.length === 0}
            />
          </div>
          <div>
            <label style={labelStyle}>Ballot Box (Round 2)</label>
            <SearchableSelect
              value={criteria.ballotR2 === 'All' ? '' : criteria.ballotR2}
              options={ballotR2Options}
              onChange={v => setField('ballotR2', v || 'All')}
              placeholder="All Boxes"
              disabled={ballotR2Options.length === 0}
            />
          </div>
        </div>
      </FilterGroup>

      <FilterGroup icon="user" title="Demographics" activeCount={groupActiveCount('demo')}>
        {/* Three equal cells: Age min | Age max | Gender. Each is the
            same width. Occupation lives in the Political group. */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
          <div>
            <label style={labelStyle}>Age min</label>
            <input type="number" value={criteria.ageMin} onChange={e => setField('ageMin', e.target.value)} placeholder="Min" style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>Age max</label>
            <input type="number" value={criteria.ageMax} onChange={e => setField('ageMax', e.target.value)} placeholder="Max" style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>Gender</label>
            <SearchableSelect
              value={criteria.gender === 'All' ? '' : criteria.gender}
              options={GENDER_OPTIONS}
              onChange={v => setField('gender', v || 'All')}
              placeholder="All"
            />
          </div>
        </div>
      </FilterGroup>

      <FilterGroup icon="vote" title="Political" activeCount={groupActiveCount('political')}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 10, marginBottom: 12 }}>
          {canSeeSupport && (
            <div>
              <label style={labelStyle}>Support Level</label>
              <SearchableSelect
                value={criteria.support === 'All' ? '' : criteria.support}
                options={SUPPORT_LEVELS}
                onChange={v => setField('support', v || 'All')}
                placeholder="All Levels"
              />
            </div>
          )}
          <div>
            <label style={labelStyle}>Party Status</label>
            <SearchableSelect
              value={criteria.party === 'All' ? '' : criteria.party}
              options={Object.entries(PARTY_META).map(([k, label]) => ({ value: k, label }))}
              onChange={v => setField('party', v || 'All')}
              placeholder="All Parties"
            />
          </div>
          <div>
            <label style={labelStyle}>Occupation</label>
            <SearchableSelect
              value={criteria.occupation === 'All' ? '' : criteria.occupation}
              options={OCCUPATION_CATEGORIES}
              onChange={v => setField('occupation', v || 'All')}
              placeholder="All"
            />
          </div>
        </div>
        <div>
          <label style={labelStyle}>Key Issues (any of selected)</label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {ISSUE_OPTIONS.map(issue => {
              const on = criteria.issues.includes(issue);
              return (
                <button
                  key={issue}
                  onClick={() => toggleIssue(issue)}
                  style={{
                    padding: '5px 10px',
                    borderRadius: 14,
                    border: '1px solid ' + (on ? '#3b82f6' : 'var(--border)'),
                    background: on ? 'var(--border)' : 'transparent',
                    color: on ? 'var(--link)' : 'var(--text-2)',
                    cursor: 'pointer',
                    fontSize: 10,
                    fontFamily: "'DM Mono', monospace",
                  }}>
                  {on ? '✓ ' : ''}{issue}
                </button>
              );
            })}
            <button
              onClick={() => setField('influencerOnly', !criteria.influencerOnly)}
              style={{
                padding: '5px 10px',
                borderRadius: 14,
                border: '1px solid ' + (criteria.influencerOnly ? '#3b82f6' : 'var(--border)'),
                background: criteria.influencerOnly ? 'var(--border)' : 'transparent',
                color: criteria.influencerOnly ? 'var(--link)' : 'var(--text-2)',
                cursor: 'pointer',
                fontSize: 10,
                fontFamily: "'DM Mono', monospace",
                display: 'inline-flex',
                alignItems: 'center',
                gap: 5,
              }}>
              {criteria.influencerOnly && '✓'}<Icon name="crown" size={11} /> Key Influencers
            </button>
          </div>
        </div>
      </FilterGroup>

      <FilterGroup icon="phone" title="Outreach Status" activeCount={groupActiveCount('outreach')}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 10 }}>
          <div>
            <label style={labelStyle}>Has been contacted</label>
            <SearchableSelect
              value={criteria.contacted === 'Any' ? '' : criteria.contacted}
              options={['Yes', 'No (never contacted)']}
              onChange={v => setField('contacted', v ? (v === 'No (never contacted)' ? 'No' : v) : 'Any')}
              placeholder="Any"
            />
          </div>
          <div>
            <label style={labelStyle}>Last contact</label>
            <SearchableSelect
              value={criteria.lastContactWithin === 'Any' ? '' : criteria.lastContactWithin}
              options={[
                { value: '7', label: 'Within 7 days' },
                { value: '30', label: 'Within 30 days' },
                { value: '90', label: 'Within 90 days' },
                { value: 'Never', label: 'Never contacted' },
              ]}
              onChange={v => setField('lastContactWithin', v || 'Any')}
              placeholder="Any time"
            />
          </div>
          <div>
            <label style={labelStyle}>Outreach type</label>
            <SearchableSelect
              value={criteria.outreachType === 'All' ? '' : criteria.outreachType}
              options={OUTREACH_TYPES}
              onChange={v => setField('outreachType', v || 'All')}
              placeholder="All types"
            />
          </div>
        </div>
      </FilterGroup>

      <FilterGroup icon="users" title="Family" activeCount={groupActiveCount('family')}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 10 }}>
          <div>
            <label style={labelStyle}>Has family on file</label>
            <SearchableSelect
              value={criteria.hasFamily === 'Any' ? '' : criteria.hasFamily}
              options={['Yes', 'No']}
              onChange={v => setField('hasFamily', v || 'Any')}
              placeholder="Any"
            />
          </div>
          <div>
            <label style={labelStyle}>
              Specific family {criteria.island === 'All' && <span style={{ color: 'var(--text-4)' }}>(pick island in Location first)</span>}
            </label>
            <SearchableSelect
              value={criteria.familyId === 'All' ? '' : String(criteria.familyId)}
              options={familiesForLocation
                .map(f => ({ value: String(f.id), label: familyNamesById[f.id] || '(unnamed family)' }))
                .sort((a, b) => a.label.localeCompare(b.label))}
              onChange={v => setField('familyId', v || 'All')}
              placeholder="All families"
              disabled={criteria.island === 'All' || familiesForLocation.length === 0}
            />
          </div>
          <div>
            <label style={labelStyle}>Family role</label>
            <SearchableSelect
              value={criteria.familyRole === 'All' ? '' : criteria.familyRole}
              options={familyRoleOptions}
              onChange={v => setField('familyRole', v || 'All')}
              placeholder="Any role"
              disabled={familyRoleOptions.length === 0}
            />
          </div>
        </div>
      </FilterGroup>

      </>}

      {/* ───── Active filter pills + reset (advanced mode only) ───── */}
      {!isSimpleMode && hasAnyCriteria && (
        <div style={{
          background: 'var(--surface)',
          border: '1px solid var(--border)',
          borderRadius: 8,
          padding: 10,
          marginTop: 4,
          marginBottom: 12,
          display: 'flex',
          flexWrap: 'wrap',
          gap: 6,
          alignItems: 'center',
        }}>
          {activePills.map(pill => (
            <button
              key={pill.key}
              onClick={() => removePill(pill)}
              style={{
                padding: '4px 9px',
                borderRadius: 14,
                background: 'var(--border)',
                border: '1px solid #3b82f6',
                color: 'var(--link)',
                cursor: 'pointer',
                fontSize: 10,
                fontFamily: "'DM Mono', monospace",
                display: 'inline-flex',
                alignItems: 'center',
                gap: 5,
              }}>
              <span>{pill.label}</span>
              <span style={{ color: 'var(--link)', fontWeight: 700 }}>×</span>
            </button>
          ))}
          <button
            onClick={() => { setCriteria(EMPTY_CRITERIA); setShowAll(false); }}
            style={{
              padding: '4px 9px',
              borderRadius: 14,
              background: 'transparent',
              border: '1px dashed #ef4444',
              color: '#ef4444',
              cursor: 'pointer',
              fontSize: 10,
              fontFamily: "'DM Mono', monospace",
              marginLeft: 'auto',
            }}>
            Reset all
          </button>
        </div>
      )}

      {/* ───── Results / Empty state ───── */}
      {(!isSimpleMode && !hasAnyCriteria) ? (
        <div style={{
          background: 'var(--surface)',
          border: '1px dashed var(--border)',
          borderRadius: 8,
          padding: '40px 20px',
          textAlign: 'center',
        }}>
          <div style={{ display: 'inline-grid', placeItems: 'center', width: 56, height: 56, borderRadius: 14, background: 'var(--surface-2)', color: 'var(--text-4)', marginBottom: 12 }}><Icon name="search" size={26} /></div>
          <div style={{
            color: 'var(--text)',
            fontFamily: "'Cormorant Garamond', serif",
            fontSize: 17,
            marginBottom: 4,
          }}>
            Start by searching above
          </div>
          <div style={{
            color: 'var(--text-3)',
            fontFamily: "'DM Mono', monospace",
            fontSize: 11,
          }}>
            Or open a filter group to narrow down voters by location, demographics, support, outreach, or family.
          </div>
        </div>
      ) : (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap', margin: '4px 2px 10px 2px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', fontSize: 11, color: 'var(--text-2)', fontFamily: "'DM Mono', monospace" }}>
              <span>Found <strong style={{ color: 'var(--link)' }}>{filteredVoters.length}</strong> of {allVoters.length} voters</span>
              {canSeeSupport && filteredVoters.length > 0 && (
                <span style={{ display: 'inline-flex', gap: 10 }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><span style={{ width: 7, height: 7, borderRadius: 2, background: '#10b981' }} />{resultSummary.sup}</span>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><span style={{ width: 7, height: 7, borderRadius: 2, background: 'var(--text-3)' }} />{resultSummary.und}</span>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><span style={{ width: 7, height: 7, borderRadius: 2, background: '#ef4444' }} />{resultSummary.agn}</span>
                </span>
              )}
            </div>
            {filteredVoters.length > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <select value={sortBy} onChange={e => setSortBy(e.target.value)}
                  style={{ padding: '7px 10px', borderRadius: 9, border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-2)', fontSize: 12, fontWeight: 600, fontFamily: "'DM Mono', monospace", cursor: 'pointer', outline: 'none' }}>
                  <option value="name">Sort: Name</option>
                  <option value="island">Sort: Island</option>
                  <option value="age">Sort: Age</option>
                  {canSeeSupport && <option value="support">Sort: Support</option>}
                </select>
                <button onClick={() => exportCsv(sortedVoters)} title="Export all results to CSV"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 12px', borderRadius: 9, border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-2)', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: "'DM Mono', monospace" }}>
                  <Icon name="download" size={14} /> Export CSV
                </button>
              </div>
            )}
          </div>

          {filteredVoters.length === 0 ? (
            <div style={{
              background: 'var(--surface)',
              border: '1px solid var(--border)',
              borderRadius: 8,
              padding: 30,
              textAlign: 'center',
              color: 'var(--text-3)',
              fontFamily: "'DM Mono', monospace",
              fontSize: 11,
            }}>
              No voters match these filters. Try removing a pill above.
            </div>
          ) : (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
              gap: 12,
            }}>
              {sortedVoters.map(voter => {
                const supportColor = canSeeSupport ? (SC[voter.support] || SC['Undecided']) : SC['Undecided'];
                return (
                  <div
                    key={voter.id}
                    onClick={() => navigate(`/voter/${voter.id}`)}
                    style={{
                      background: 'var(--surface)',
                      border: '1px solid var(--border)',
                      boxShadow: 'var(--shadow-sm)',
                      borderRadius: 12,
                      padding: 16,
                      cursor: 'pointer',
                      position: 'relative',
                      transition: 'transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease',
                    }}
                    onMouseEnter={e => {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = 'var(--shadow-md)';
                      e.currentTarget.style.borderColor = '#3b82f6';
                    }}
                    onMouseLeave={e => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = 'var(--shadow-sm)';
                      e.currentTarget.style.borderColor = 'var(--border)';
                    }}
                  >
                    <div style={{
                      fontFamily: "'Cormorant Garamond', serif",
                      fontSize: 16,
                      fontWeight: 600,
                      color: 'var(--text-strong)',
                      marginBottom: 6,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}>
                      {voter.name}
                    </div>
                    <div style={{
                      fontSize: 10.5,
                      color: 'var(--text-3)',
                      fontFamily: "'DM Mono', monospace",
                      lineHeight: 1.7,
                      minWidth: 0,
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 5, minWidth: 0 }}>
                        <Icon name="mapPin" size={11} style={{ flexShrink: 0 }} />
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {voter.island}{voter.ward ? `, ${voter.ward}` : ''}
                        </span>
                      </div>
                      {voter.house && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 5, minWidth: 0 }}>
                          <Icon name="home" size={11} style={{ flexShrink: 0 }} />
                          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{voter.house}</span>
                        </div>
                      )}
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginTop: 1 }}>
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>Age {voter.age ?? calculateAge(voter.dob) ?? '—'} · {voter.gender || '—'}</span>
                        {canSeeSupport && voter.support && (
                          <span style={{ flexShrink: 0, padding: '2px 8px', borderRadius: 10, fontSize: 10, fontWeight: 600, background: supportColor.bg, color: supportColor.text }}>{voter.support}</span>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
}
